import { useEffect, useState } from "react";
import { Download, Share2, Smartphone, Check, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS
    window.navigator.standalone === true);

export function InstallAppButton({ variant = "icon" }) {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());
  const [iosHelpOpen, setIosHelpOpen] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
      setOpen(false);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch {}
      setDeferred(null);
      setOpen(false);
    } else if (isIOS()) {
      setIosHelpOpen(true);
      setOpen(false);
    }
  };

  // Already installed → hide entirely
  if (installed) return null;

  // Nothing to show if browser doesn't support install AND not iOS
  const canInstall = !!deferred || isIOS();
  if (!canInstall) return null;

  const trigger =
    variant === "text" ? (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 border border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary px-3 h-8 text-[11px] font-bold uppercase tracking-widest"
        data-testid="install-app-button"
        title="Instalar app"
      >
        <Download size={13} /> Instalar app
      </button>
    ) : (
      <button
        onClick={() => setOpen(true)}
        className="p-1.5 border hover:bg-muted"
        data-testid="install-app-button"
        title="Instalar app"
      >
        <Download size={14} />
      </button>
    );

  return (
    <>
      {trigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="uppercase font-black text-sm flex items-center gap-2">
              <Smartphone size={16} /> Instalar MFIS Supply-Chain
            </DialogTitle>
            <DialogDescription className="text-xs">
              Instala esta app en tu dispositivo para tenerla como un ícono en la pantalla de inicio, abrirla sin
              barra del navegador y usarla con acceso rápido a Escaneo QR y OTs.
            </DialogDescription>
          </DialogHeader>
          <ul className="text-[11px] space-y-2">
            <li className="flex items-start gap-2"><Check size={12} className="text-emerald-600 mt-0.5" /> Icono en pantalla de inicio (móvil y escritorio)</li>
            <li className="flex items-start gap-2"><Check size={12} className="text-emerald-600 mt-0.5" /> Modo pantalla completa (sin barras del navegador)</li>
            <li className="flex items-start gap-2"><Check size={12} className="text-emerald-600 mt-0.5" /> Atajos directos: Escanear · Nueva OT · Requisiciones</li>
            <li className="flex items-start gap-2"><Check size={12} className="text-emerald-600 mt-0.5" /> Funciona en Android, iOS, Windows, macOS y ChromeOS</li>
          </ul>
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none text-xs" data-testid="install-cancel">
              <X size={12} className="mr-1" /> Ahora no
            </Button>
            <Button onClick={install} className="rounded-none text-xs font-bold" data-testid="install-confirm">
              <Download size={12} className="mr-1" /> {deferred ? "Instalar" : "Ver instrucciones"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iOS-specific dialog with visual instructions */}
      <Dialog open={iosHelpOpen} onOpenChange={setIosHelpOpen}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="uppercase font-black text-sm flex items-center gap-2">
              <Smartphone size={16} /> Instalar en iPhone / iPad
            </DialogTitle>
            <DialogDescription className="text-xs">
              Safari no ofrece un botón automático. Sigue estos 3 pasos:
            </DialogDescription>
          </DialogHeader>
          <ol className="text-[12px] space-y-3">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 border-2 border-primary text-primary font-black flex items-center justify-center text-[11px]">1</span>
              <span>Toca el botón <b>Compartir</b> <Share2 size={12} className="inline align-text-bottom text-blue-600" /> en la parte inferior de Safari.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 border-2 border-primary text-primary font-black flex items-center justify-center text-[11px]">2</span>
              <span>Desplázate y toca <b>"Añadir a pantalla de inicio"</b>.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 border-2 border-primary text-primary font-black flex items-center justify-center text-[11px]">3</span>
              <span>Confirma con <b>"Añadir"</b> en la esquina superior derecha. El ícono aparecerá en tu pantalla de inicio.</span>
            </li>
          </ol>
          <DialogFooter>
            <Button onClick={() => setIosHelpOpen(false)} className="rounded-none text-xs font-bold" data-testid="ios-help-close">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
